// Copyright (c) 2025 Audere, All Rights Reserved

import { useTranslation } from "react-i18next";
import React, { memo } from "react";
import { FixedSizeList as List } from "react-window";
import { ListItem, IconButton, LinearProgress, Chip, Box, Typography, Paper } from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Error from "@mui/icons-material/Error";
import Pending from "@mui/icons-material/Pending";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import CloudUpload from "@mui/icons-material/CloudUpload";
import Info from "@mui/icons-material/Info";
import { FileStatus, type FileInfo, type VirtualizedFileListProps } from "../types/projects";
import { formatFileSize } from "../utils/workerUtils";
import { createStyleSheet } from "../utils/styles";
import { UPLOAD_TEST_ITEM_HEIGHT } from "../utils/constants";

const VirtualizedFileList: React.FC<VirtualizedFileListProps> = memo(
  ({ files, onRemoveFile, height = 400, onScrollToTop }) => {
    const { t } = useTranslation();
    const getStatusIcon = (status: FileInfo["status"]) => {
      switch (status) {
        case FileStatus.Pending:
          return <Pending color="action" />;
        case FileStatus.Checking:
          return <Info color="info" />;
        case FileStatus.Uploading:
          return <CloudUpload color="warning" />;
        case FileStatus.Completed:
          return <CheckCircle color="success" />;
        case FileStatus.Exists:
          return <CheckCircle color="info" />;
        case FileStatus.Error:
          return <Error color="error" />;
        case FileStatus.Invalid:
          return <ImageNotSupportedIcon color="error" />;
        default:
          return <Pending color="action" />;
      }
    };

    const getStatusColor = (status: FileInfo["status"]) => {
      switch (status) {
        case FileStatus.Pending:
          return "default";
        case FileStatus.Checking:
          return "info";
        case FileStatus.Uploading:
          return "warning";
        case FileStatus.Invalid:
          return "error";
        case FileStatus.Completed:
          return "success";
        case FileStatus.Exists:
          return "info";
        case FileStatus.Error:
          return "error";
        default:
          return "default";
      }
    };

    const getStatusText = (status: FileInfo["status"]) => {
      switch (status) {
        case FileStatus.Pending:
          return t("status:pending");
        case FileStatus.Checking:
          return t("status:checking");
        case FileStatus.Uploading:
          return t("status:uploading");
        case FileStatus.Completed:
          return t("status:completed");
        case FileStatus.Exists:
          return t("status:exists");
        case FileStatus.Error:
          return t("status:error");
        case FileStatus.Invalid:
          return t("status:invalid");
        default:
          return t("status:unknown");
      }
    };

    const renderFileItem = ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const file = files[index];

      return (
        <Box sx={style}>
          <ListItem divider sx={styles.listItem}>
            <Box sx={styles.fileRow}>
              {getStatusIcon(file.status)}
              <Box sx={styles.fileInfoWrapper}>
                <Box sx={styles.fileNameRow}>
                  <Typography component="span" variant="body1" noWrap sx={styles.fileName}>
                    {file.filename}
                  </Typography>
                  <Chip
                    label={getStatusText(file.status)}
                    color={getStatusColor(file.status)}
                    size="small"
                  />
                </Box>
                <Box sx={styles.fileDetails}>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {formatFileSize(file.size)}
                  </Typography>
                  {file.error && (
                    <Typography component="span" variant="body2" color="error">
                      {file.error}
                    </Typography>
                  )}
                  {(file.status === "uploading" || file.status === "checking") && (
                    <LinearProgress
                      variant={file.status === "uploading" ? "determinate" : "indeterminate"}
                      value={file.progress}
                    />
                  )}
                </Box>
              </Box>
              <IconButton edge="end" aria-label="delete" onClick={() => onRemoveFile(file)}>
                <DeleteForeverIcon color="error" />
              </IconButton>
            </Box>
          </ListItem>
        </Box>
      );
    };
    return (
      <Paper>
        {files && files.length > 0 ? (
          <List
            height={Math.min(height, files.length * UPLOAD_TEST_ITEM_HEIGHT)}
            itemCount={files.length}
            itemSize={UPLOAD_TEST_ITEM_HEIGHT}
            width="100%"
            overscanCount={5}
            onScroll={({ scrollOffset }) => {
              if (scrollOffset > 0 && onScrollToTop) {
                onScrollToTop();
              }
            }}
          >
            {renderFileItem}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", p: 2 }}>
            {t("common:noFiles")}
          </Typography>
        )}
      </Paper>
    );
  }
);

export default VirtualizedFileList;

const styles = createStyleSheet({
  listItem: {
    height: UPLOAD_TEST_ITEM_HEIGHT,
    padding: 2,
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  fileInfoWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    marginLeft: 1,
  },
  fileNameRow: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  fileName: {
    maxWidth: 300,
  },
  fileDetails: {
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
  },
  noFilesPaper: {
    padding: 3,
    textAlign: "center",
  },
});
